// src/pages/ordens-servico/OrdensServicoPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { escopoPermissao, pode, temNivelMinimo } from "../../utils/permissoes";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import FiltroBar from "../../components/FiltroBar/FiltroBar";

import { db } from "../../config/firebase";
import { NIVEIS } from "../../config/niveis";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";

import {
  IconCalendar,
  IconList,
  IconSector,
  IconBuilding,
  IconKey,
  IconTool,
  IconClock,
} from "../../components/Icons/Icons";



function renderCodigoOs(os) {
  if (os.codigo) return os.codigo;
  if (os.codigoSequencial != null) {
    return `OS${String(os.codigoSequencial).padStart(4, "0")}`;
  }
  if (os.id) return os.id.slice(0, 8);
  return "-";
}

function getCriticidadeConfig(criticidade) {
  const valor = (criticidade || "").toLowerCase();

  if (valor === "alta" || valor === "muito alta") {
    return {
      texto: "Alta",
    };
  }

  if (valor === "média" || valor === "media") {
    return {
      texto: "Média",
    };
  }

  if (valor === "baixa") {
    return {
      texto: "Baixa",
    };
  }

  return null;
}

// Usa a cor configurada no status (hex) para montar um pequeno chip visual.
function getStatusStyle(corHex) {
  if (!corHex) return null;

  const cor = corHex;

  return {
    corFundo: "var(--cor-card)",
    corTexto: "var(--cor-texto)",
    corBorda: cor,
  };
}

function formatarData(ts) {
  if (!ts?.toDate) return "-";
  const d = ts.toDate();
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Normaliza texto removendo acentos e deixando minúsculo.
function normalizarTexto(txt) {
  return String(txt || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

// Mantém compatibilidade com OS antigas que gravavam apenas statusNome ou usavam ids antigos.
function resolverStatusId(os) {
  const idBruto = os?.statusId || os?.status || "";
  const idNorm = normalizarTexto(idBruto);

  // Aliases antigos
  const aliasId = {
    "fab_aguard_material": "fab_aguard_mat",
    "fab_aguardando_material": "fab_aguard_mat",
    "fab_solic_material": "fab_solic_mat",
    "fab_solicitacao_material": "fab_solic_mat",
    "fab_aguard_parada_maquina": "fab_aguard_parada",
  };

  if (idNorm && aliasId[idNorm]) return aliasId[idNorm];
  if (idBruto) return idBruto;

  // Se não tem id, tenta inferir pelo nome
  const nomeNorm = normalizarTexto(os?.statusNome);
  const nomeParaId = {
    "aberta": "fab_aberta",
    "aberta 24h": "fab_aberta_24h",
    "em atendimento": "fab_em_atendimento",
    "finalizada": "fab_finalizada",
    "finalizado": "fab_finalizada",
    "cancelada": "fab_cancelada",
    "cancelado": "fab_cancelada",
    "aguardando material": "fab_aguard_mat",
    "solicitacao de material": "fab_solic_mat",
    "solicitação de material": "fab_solic_mat",
    "aguardando parada de maquina": "fab_aguard_parada",
    "aguardando parada de máquina": "fab_aguard_parada",
  };

  return nomeParaId[nomeNorm] || "sem-status";
}

function OrdensServicoPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const podeVerPorStatus = pode(usuarioLogado, "ordensServico", "porStatus", "tabela");
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  // 🔒 Apenas Técnico+ pode iniciar atendimento (independente de customizações)
  const podeIniciarAtendimento = temNivelMinimo(usuarioLogado, NIVEIS.TECNICO);

  const [ordens, setOrdens] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [view, setView] = useState("status"); // 'status' | 'lista'
  const [statusSelecionadoId, setStatusSelecionadoId] = useState(null);
  const [campoFiltro, setCampoFiltro] = useState("codigo");
  const [textoFiltro, setTextoFiltro] = useState("");

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "titulo", label: "Título" },
    { value: "empresaNome", label: "Empresa" },
    { value: "setorNome", label: "Setor" },
    { value: "maquinaNome", label: "Máquina" },
{ value: "criadoPorNome", label: "Aberta por" },
  ];

  useEffect(() => {
    if (!usuarioLogado?.grupoId) return;

    setCarregando(true);
    setErro("");

    async function carregarStatusList() {
      try {
        const statusFabrica = [
          {id:"fab_aberta", nome:"Aberta", cor:"#2563eb", padrao:true},
          {id:"fab_aberta_24h", nome:"Aberta 24H", cor:"#7c3aed", padrao:true},
          {id:"fab_em_atendimento", nome:"Em Atendimento", cor:"#0ea5e9", padrao:true},
          {id:"fab_finalizada", nome:"Finalizada", cor:"#16a34a", padrao:true},
          {id:"fab_cancelada", nome:"Cancelada", cor:"#dc2626", padrao:true},
          {id:"fab_aguard_mat", nome:"Aguardando Material", cor:"#ca8a04", padrao:true},
          {id:"fab_solic_mat", nome:"Solicitação de Material", cor:"#d97706", padrao:true},
          {id:"fab_aguard_parada", nome:"Aguardando Parada de Máquina", cor:"#64748b", padrao:true}
        ];

        const statusRef = collection(db, "statusOs");
        const qStatus = query(statusRef, where("grupoId", "==", usuarioLogado.grupoId));
        const snapStatus = await getDocs(qStatus);
        const listaStatusDb = snapStatus.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const listaStatus = [...statusFabrica, ...listaStatusDb];
        listaStatus.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || "")));
        setStatusList(listaStatus);
      } catch (e) {
        console.error("Erro ao carregar status:", e);
      }
    }

    carregarStatusList();

    const osRef = collection(db, "ordensServico");
    // ✅ IMPORTANTÍSSIMO: o recurso aqui precisa bater com a tabela de permissões.
    // Esta tela é "Ordens de Serviço - Por Status" (ordensServico.porStatus.tabela)
    const escopo = escopoPermissao(usuarioLogado, "ordensServico", "porStatus", "tabela");
    const filtros = [where("grupoId", "==", usuarioLogado.grupoId)];
    if (escopo === "proprias" && usuarioLogado?.uid) {
      filtros.push(where("criadoPor", "==", usuarioLogado.uid));
    }
    const qOs = query(osRef, ...filtros);

    const unsubscribe = onSnapshot(
      qOs,
      (snapOs) => {
        const listaOs = snapOs.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setOrdens(listaOs);
        setCarregando(false);
      },
      (error) => {
        console.error("Erro ao escutar OS:", error);
        setErro("Erro ao carregar Ordens de Serviço. Tente novamente.");
        setCarregando(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  const contagemPorStatus = useMemo(() => {
    const mapa = {};
    for (const os of ordens) {
      const idSt = resolverStatusId(os);
      mapa[idSt] = (mapa[idSt] || 0) + 1;
    }
    return mapa;
  }, [ordens]);

  const statusSelecionado = useMemo(
    () => statusList.find((st) => st.id === statusSelecionadoId) || null,
    [statusSelecionadoId, statusList]
  );

  const ordensDoStatusSelecionado = useMemo(() => {
    if (!statusSelecionadoId) return [];

    const texto = textoFiltro.trim().toLowerCase();
    const stSelecionado = statusList.find((st) => st.id === statusSelecionadoId);
    const nomeSelecionadoNorm = normalizarTexto(stSelecionado?.nome);

    return ordens
      .filter((os) => {
        // Primeiro tenta pelo id (com aliases). Se OS antiga não tiver id, cai para comparação por nome.
        const idOs = resolverStatusId(os);
        if (idOs === statusSelecionadoId) return true;
        if (idOs === "sem-status") {
          const nomeOsNorm = normalizarTexto(os.statusNome);
          return nomeOsNorm && nomeSelecionadoNorm && nomeOsNorm === nomeSelecionadoNorm;
        }
        return false;
      })
      .filter((os) => {
        if (!texto) return true;
        const mapaCampos = {
          codigo: renderCodigoOs(os),
          titulo: os.titulo || os.descricao || "",
          empresaNome: os.empresaNome || "",
          setorNome: os.setorNome || "",
          maquinaNome: os.maquinaNome || "",
criadoPorNome: os.criadoPorNome || "",
        };
        const valorCampo = mapaCampos[campoFiltro] ?? "";
        return String(valorCampo).toLowerCase().includes(texto);
      })
      .sort((a, b) => {
        const tA = a.criadoEm?.toMillis?.() ?? 0;
        const tB = b.criadoEm?.toMillis?.() ?? 0;
        return tA - tB;
      });
  }, [ordens, statusSelecionadoId, textoFiltro, campoFiltro, statusList]);

  function handleIniciarAtendimento(osId) {
    if (!osId) return;

    // 🔒 Segurança: Cliente/Kiosk não deve iniciar atendimento
    if (!podeIniciarAtendimento) return;

    // Respeita também a matriz de permissões (se alguém subir o mínimo acima de Técnico)
    const canIniciar = pode(usuarioLogado, "ordensServico", "meusAtendimentos", "tabela");
    if (!canIniciar) return;

    navigate(`/ordens-servico/atendimento/${osId}`);
  }

  function handleAbrirStatus(statusId) {
    setStatusSelecionadoId(statusId);
    setView("lista");
  }

  function handleVoltarStatus() {
    setStatusSelecionadoId(null);
    setView("status");
    setTextoFiltro("");
  }

  function renderStatusCard(st, index) {
    const total = contagemPorStatus[st.id] || 0;
    const temItens = total > 0;
    const corStatus = st.cor || "#64748b";

    return (
      <article
        key={st.id}
        className="status-card-modern"
        style={{
          cursor: temItens ? "pointer" : "default",
          opacity: temItens ? 1 : 0.4,
          animationDelay: `${index * 0.05}s`,
          "--status-card-bg-dyn": `linear-gradient(135deg, ${corStatus}15 0%, var(--cor-card) 100%)`,
          "--status-card-border-left-dyn": `5px solid ${corStatus}`,
        }}
        onClick={() => temItens && handleAbrirStatus(st.id)}
      >
        <div className="status-card-content">
          <div className="status-card-icon" style={{ background: corStatus }}>
            <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
              {total}
            </span>
          </div>
          
          <div className="status-card-info">
            <h4 className="status-card-title">{st.nome}</h4>
            <p className="status-card-subtitle">
              {temItens
                ? `${total} ${total === 1 ? "ordem de serviço" : "ordens de serviço"}`
                : "Nenhuma OS neste status"}
            </p>
          </div>

          <div className="status-card-action">
            {temItens ? (
              <button
                type="button"
                className="status-card-btn"
                style={{ 
                  background: `linear-gradient(135deg, ${corStatus}, ${corStatus}cc)`,
                  boxShadow: `0 4px 15px ${corStatus}40`
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAbrirStatus(st.id);
                }}
              >
                <IconList size="14px" />
                <span>Ver<br/>Ordens</span>
              </button>
            ) : (
              <span style={{ 
                fontSize: "0.8rem", 
                color: "var(--cor-texto-apagado)",
                fontStyle: "italic" 
              }}>
                Vazio
              </span>
            )}
          </div>
        </div>

        {temItens && (
          <div 
            className="status-card-progress"
            style={{ background: corStatus }}
          />
        )}
      </article>
    );
  }

  function getCriticidadeBadgeClass(crit) {
    const valor = (crit || "").toLowerCase();
    if (valor === "alta" || valor === "muito alta") return "badge-alta";
    if (valor === "média" || valor === "media") return "badge-media";
    if (valor === "baixa") return "badge-baixa";
    return "";
  }

  function renderOsCard(os, index) {
    const cfgCrit = getCriticidadeConfig(os.criticidade);
    const critBadgeClass = getCriticidadeBadgeClass(os.criticidade);
    const corStatus = os.statusCor || "#64748b";
    const fotoUrl = os.fotoAberturaUrl || (Array.isArray(os.fotosAbertura) ? os.fotosAbertura[0] : null);
    const paradaTxt = (typeof os?.maquinaParadaAtiva === "boolean")
      ? os.maquinaParadaAtiva
      : (String(os.maquinaParou || "-").toLowerCase().trim() === "sim" && !os?.maquinaVoltouRodarEm);

    return (
      <article
        key={os.id}
        className="os-card-modern"
        style={{ 
          animationDelay: `${index * 0.04}s`,
          borderLeft: `4px solid ${corStatus}`,
        }}
      >
        <div className="os-card-header">
          <div className="os-card-avatar" style={{ background: `${corStatus}30`, borderColor: corStatus }}>
            {os.maquinaNome?.[0] || "O"}
          </div>
          
          <div className="os-card-title-area">
            <div className="os-card-code">{renderCodigoOs(os)}</div>
            <h4 className="os-card-machine">
              {os.maquinaNome || "Sem máquina"}
            </h4>
          </div>

          {fotoUrl && (
            <img
              className="os-card-photo"
              src={fotoUrl}
              alt="Foto da OS"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}

          <div className="os-card-badges">
            {cfgCrit && (
              <span className={`os-badge ${critBadgeClass}`}>
                {cfgCrit.texto}
              </span>
            )}
            {paradaTxt && (
              <span className="os-badge badge-parou">
                Parou
              </span>
            )}
          </div>
        </div>

        <p className="os-card-description">
          {os.titulo || os.descricao || "Sem descrição"}
        </p>

        <div className="os-card-meta">
          <div className="os-meta-item">
            <IconBuilding size="14px" />
            <span>{os.empresaNome || "-"}</span>
          </div>
          <div className="os-meta-item">
            <IconSector size="14px" />
            <span>{os.setorNome || "-"}</span>
          </div>
          <div className="os-meta-item">
            <IconClock size="14px" />
            <span>{formatarData(os.criadoEm)}</span>
          </div>
        </div>

        <div className="os-card-footer">
          <div className="os-status-chip" style={{ borderColor: corStatus, "--os-status-chip-bg-dyn": `${corStatus}20` }}>
            <span className="os-status-dot" style={{ background: corStatus }} />
            <span>{os.statusNome || "Sem status"}</span>
          </div>
          
          {(() => {
            const statusNome = (os.statusNome || "").toLowerCase();
            const bloqueada = ["finalizada", "finalizado", "cancelada", "cancelado"].some(
              (s) => statusNome.includes(s)
            );
            if (bloqueada) {
              return (
                <span className="os-status-done">
                  ✓ {os.statusNome}
                </span>
              );
            }

            // 🔒 Cliente/Kiosk não inicia atendimento (somente Técnico+)
            if (!podeIniciarAtendimento) return null;
            const canIniciar = pode(usuarioLogado, "ordensServico", "meusAtendimentos", "tabela");
            if (!canIniciar) return null;

            return (
              <button
                type="button"
                className="os-card-action-btn"
                onClick={() => handleIniciarAtendimento(os.id)}
              >
                <IconTool size="16px" />
                Iniciar Atendimento
              </button>
            );
          })()}
        </div>
      </article>
    );
  }

  const totalOrdens = ordens.length;

  
  if (!podeVerPorStatus) {
    return (
      <LayoutDesktop tema={tema} titulo="OS por Status" subtitulo="">
        <section className="pc-card" style={{ padding: 16 }}>
          <h3>Acesso restrito</h3>
          <p>Você não tem permissão para acessar esta visão.</p>
        </section>
      </LayoutDesktop>
    );
  }

if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="OS por Status"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="os"
      >
        <div className="m-actions">
          <button
            className="m-btn-pill m-btn-secondary"
            onClick={() => navigate("/ordens-servico")}
          >
            Fila de Atendimento
          </button>
          <button className="m-btn-pill m-btn-primary">
            Por Status
          </button>
        </div>

        {erro && (
          <div className="m-error-banner">{erro}</div>
        )}

        {view === "status" ? (
          <>
            <p style={{ marginTop: 12, marginBottom: 12, fontSize: "0.85rem", opacity: 0.9 }}>
              {totalOrdens === 0
                ? "Nenhuma OS cadastrada."
                : "Toque em um status para ver as ordens correspondentes."}
            </p>
            {carregando ? (
              <div className="m-loading-state">
                <span className="m-loading-spinner" />
                <span>Carregando...</span>
              </div>
            ) : (
              <div className="status-cards-grid">
                {statusList.map((st, index) => renderStatusCard(st, index))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="m-btn-pill m-btn-secondary"
              style={{ marginBottom: 10 }}
              onClick={handleVoltarStatus}
            >
              ◀ Voltar para status
            </button>

            <h3 style={{ marginBottom: 8 }}>
              {statusSelecionado?.nome || "Status"}
            </h3>
            <p style={{ marginBottom: 12, fontSize: "0.85rem", opacity: 0.9 }}>
              {ordensDoStatusSelecionado.length} {ordensDoStatusSelecionado.length === 1 ? "ordem" : "ordens"} neste status.
            </p>

            <div className="m-search-wrapper">
              <span className="m-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Filtrar OS..."
                className="m-search-input"
                value={textoFiltro}
                onChange={(e) => setTextoFiltro(e.target.value)}
              />
            </div>

            {carregando ? (
              <div className="m-loading-state">
                <span className="m-loading-spinner" />
                <span>Carregando...</span>
              </div>
            ) : (
              <div className="m-user-list">
                {ordensDoStatusSelecionado.map((os, index) =>
                  renderOsCard(os, index)
                )}
              </div>
            )}
          </>
        )}
      </LayoutMobile>
    );
  }

  // DESKTOP
  return (
    <LayoutDesktop
      tema={tema}
      titulo="OS por Status"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Ordens de Serviço por Status</h3>
            <p>
              Visualize rapidamente quantas OS existem em cada status e inicie o atendimento direto pelos cards.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              className="pc-toggle-btn"
              onClick={() => navigate("/ordens-servico")}
            >
              <IconList size="16px" /> Fila de Atendimento
            </button>
            <button type="button" className="pc-toggle-btn active">
              <IconCalendar size="16px" /> Por Status
            </button>
          </div>
        </header>

        {erro && (
          <div className="pc-error-banner">
            {erro}
          </div>
        )}

        {view === "status" ? (
          <>
            <p
              style={{
                marginBottom: 16,
                fontSize: "0.9rem",
                color: "var(--cor-texto-apagado)",
              }}
            >
              {totalOrdens === 0
                ? "Nenhuma OS cadastrada."
                : "Clique em um status abaixo para ver a lista de ordens correspondentes."}
            </p>
            {carregando ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  opacity: 0.8,
                }}
              >
                Carregando...
              </div>
            ) : (
              <div className="status-cards-grid">
                {statusList.map((st, index) => renderStatusCard(st, index))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="os-list-header">
              <div className="os-list-header-left">
                <button
                  type="button"
                  className="os-back-btn"
                  onClick={handleVoltarStatus}
                >
                  <IconList size="16px" />
                  Voltar para Status
                </button>
                <div className="os-list-title-area">
                  <h4 className="os-list-title">{statusSelecionado?.nome || "Status"}</h4>
                  <p className="os-list-count">
                    {ordensDoStatusSelecionado.length}{" "}
                    {ordensDoStatusSelecionado.length === 1 ? "ordem" : "ordens"} neste status
                  </p>
                </div>
              </div>
              <div style={{ minWidth: 280 }}>
                <FiltroBar
                  campos={camposFiltro}
                  campoSelecionado={campoFiltro}
                  onCampoChange={setCampoFiltro}
                  textoFiltro={textoFiltro}
                  onTextoChange={setTextoFiltro}
                  onAdicionarClick={null}
                  placeholder="Filtrar OS..."
                  mostrarAdicionar={false}
                />
              </div>
            </div>

            {carregando ? (
              <div className="os-loading-state">
                <IconClock size="48px" />
                <p>Carregando ordens...</p>
              </div>
            ) : ordensDoStatusSelecionado.length === 0 ? (
              <div className="os-empty-state">
                <IconTool size="48px" />
                <p>Nenhuma OS encontrada</p>
              </div>
            ) : (
              <div className="os-cards-list">
                {ordensDoStatusSelecionado.map((os, index) =>
                  renderOsCard(os, index)
                )}
              </div>
            )}
          </>
        )}
      </section>
    </LayoutDesktop>
  );
}

export default OrdensServicoPage;
